import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { universalPostRequest } from 'common/requestHandler';
import { userTypes } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { Controller, useForm } from 'react-hook-form';

import GradientButton from '../../components/elements/GradientButton';
import HeaderComp from '../../components/elements/HeadingComponent';
import InputElement from '../../components/elements/InputElement';
import OverlayLoader from '../../components/elements/OverlayLoader';
import { regionOptions } from '../contractManagement/signup/setupContract.utils';
import { AdminInterface } from './utils';
import { validationInviteAdmin } from './validation';

const InviteAdminUser = () => {
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdminInterface>({
    resolver: yupResolver(validationInviteAdmin),
    mode: 'onChange',
    defaultValues: {
      email: '',
      phone_number: '',
      companyName: '',
      firstName: '',
      lastName: '',
      region: '',
    } as AdminInterface,
  });
  const submitHandler = async (data: AdminInterface) => {
    setLoading(true);
    const response = await universalPostRequest(APIEndpoint.inviteAdminUser, {
      email: data.email,
      phone_number: data.phone_number,
      userType: userTypes.companyAdmin,
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName,
      region: data.region,
    });

    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
      reset({} as AdminInterface);
    }
    setLoading(false);
  };
  return (
    <>
      <HeaderComp>Invite Audit company (Admin user)</HeaderComp>

      <Box display="flex" justifyContent="center" mt={5}>
        <Box
          width="80%"
          sx={{ boxShadow: '5px 10px 20px rgba(0, 0, 0, 0.1)' }}
          p={5}
          borderRadius={8}
        >
          <legend>
            <Typography variant="subtitle2">Admin user details</Typography>
          </legend>
          <form onSubmit={handleSubmit(submitHandler)}>
            <InputElement
              name="firstName"
              control={control}
              label="First name"
              type="text"
              rules={{ required: true }} // Rules can be added here
            />
            <InputElement
              name="lastName"
              control={control}
              label="Last name"
              type="text"
              rules={{ required: true }} // Rules can be added here
            />

            <InputElement
              name="email"
              control={control}
              label="Email"
              type="text"
              rules={{ required: true }} // Rules can be added here
            />
            <InputElement
              name="phone_number"
              control={control}
              label="Phone"
              type="text"
              rules={{ required: true }} // Rules can be added here
            />
            <InputElement
              name="companyName"
              control={control}
              label="Company name"
              type="text"
              rules={{ required: true }} // Rules can be added here
            />
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2">
                Where would you like your data stored?
              </Typography>
              <Tooltip
                title="To comply with data protection regulations like HIPAA, CCPA, GBLR and GDPR, please tell us where you'd like your documents to be stored."
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ color: 'primary.main' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Controller
              name="region"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth sx={{ mb: 2 }} error={!!error}>
                  <InputLabel id="region-select-label">Region</InputLabel>
                  <Select
                    {...field}
                    labelId="region-select-label"
                    label="Region"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {regionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
            <Box textAlign="right" mt={4}>
              <GradientButton type="submit" rounded loading={loading}>
                Invite admin user
              </GradientButton>
            </Box>
          </form>
        </Box>
      </Box>
      <OverlayLoader loading={loading} text="Inviting admin user..." />
    </>
  );
};

export default InviteAdminUser;
